import React, { useMemo, useState } from 'react';

import { AgencyInfo } from '@components/admin/agency/AgencyInfo';
import { RoutePlanTable } from '@components/admin/agency/RoutePlanTable';
import { StaffTable } from '@components/admin/agency/StaffTable';
import { WarehouseAddEditDialog } from '@components/admin/agency/WarehouseAddEditDialog';
import { WarehouseGrid } from '@components/admin/agency/WarehouseGrid';
import { FormTitle } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { SpinnerModal } from '@components/SpinnerModal';
import { usePostWarehouse } from '@hooks/mutation/dashboard/usePostWarehouse';
import { useAgency } from '@hooks/queries/dashboard/useAgency';
import { useAgencyStaffs } from '@hooks/queries/dashboard/useAgencyStaffs';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useWarehouses } from '@hooks/queries/dashboard/useWarehouses';
import Collapse from '@material-ui/core/Collapse';
import { warehouseInitialValues } from '@models/Warehouse';
import { makeMuiStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { getBreadcrumb } from '@utils/pages';
import { useLocalStorageState } from '@utils/useLocalStorageState';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: '代理店詳細',
    agencyInfo: '代理店情報',
    warehouseList: '倉庫一覧',
    routeList: 'ルート一覧',
    staffList: 'スタッフ一覧',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    flex: 1,
    padding: '1.5rem 2rem',
    ...common.verticalSpace3,
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    marginTop: 36,
    alignItems: 'flex-end',
  },
}));

interface AgencyDetailsProps {
  agencyId?: number;
  isAdmin: boolean;
  isLoading?: boolean;
}

export const AgencyDetailsPage: React.FC<AgencyDetailsProps> = (props) => {
  const { agencyId, isLoading = false, isAdmin } = props;
  const classes = useStyles();

  const useExpanded = (name: string) => {
    return useLocalStorageState(`AGENCY_DETAILS_${name}`, z.boolean(), true);
  };

  const [agencyInfoSectionExpanded, setAgencyInfoSectionExpanded] =
    useExpanded('AGENCY_INFO_EXPANDED');
  const [warehouseSectionExpanded, setWarehouseSectionExpanded] = useExpanded('WAREHOUSE_EXPANDED');
  const [routeSectionExpanded, setRouteSectionExpanded] = useExpanded('ROUTE_EXPANDED');
  const [staffSectionExpanded, setStaffSectionExpanded] = useExpanded('STAFF_EXPANDED');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const agency = useAgency(agencyId!, { enabled: !!agencyId });
  const warehouses = useWarehouses({ agency_id: agencyId }, { enabled: !!agencyId });
  const routePlans = useRoutePlanList({ agency_id: [agencyId!] }, { enabled: !!agencyId });
  const staffList = useAgencyStaffs(agencyId!, { enabled: !!agencyId });
  const postWarehouse = usePostWarehouse();

  const breadcrumb = useMemo(
    () =>
      isAdmin
        ? [
            ...getBreadcrumb('/admin/widgets/Agencies'),
            agency.data?.name_en_short ?? String(agencyId),
          ]
        : getBreadcrumb('/admin/agency/AgencyDetails'),
    [agencyId, agency.data?.name_en_short, isAdmin],
  );

  const loading = agency.isLoading || warehouses.isLoading || isLoading;
  const errors = [agency.error, warehouses.error];

  return (
    <Layout title={strings.title} loading={loading} errors={errors} breadcrumb={breadcrumb}>
      <div className={classes.container}>
        <SpinnerModal open={postWarehouse.isLoading} />
        <WarehouseAddEditDialog
          open={isEditDialogOpen}
          initialValues={{ ...warehouseInitialValues, agency_id: agencyId! }}
          action="edit"
          onClose={() => setIsEditDialogOpen(false)}
          onSubmit={async (values) => {
            await postWarehouse.mutateAsync(values);
            setIsEditDialogOpen(false);
          }}
        />
        <FormTitle
          title={strings.agencyInfo}
          noEdit
          noExpand={false}
          expanded={agencyInfoSectionExpanded}
          onChangeExpanded={(expanded) => {
            setAgencyInfoSectionExpanded(expanded);
          }}
        />
        <Collapse in={agencyInfoSectionExpanded}>
          <AgencyInfo agency={agency.data} />
        </Collapse>
        <FormTitle
          title={strings.routeList}
          noEdit
          noExpand={false}
          expanded={routeSectionExpanded}
          onChangeExpanded={(expanded) => {
            try {
              // localStorage.setItem('routeSectionExpanded', expanded.toString());
            } catch {} // eslint-disable-line no-empty
            setRouteSectionExpanded(expanded);
          }}
        />
        <Collapse in={routeSectionExpanded}>
          <RoutePlanTable editable={isAdmin} routePlans={routePlans.data ?? []} />
        </Collapse>
        <FormTitle
          title={strings.warehouseList}
          onCreate={() => setIsEditDialogOpen(true)}
          noCreate={isAdmin ? false : true}
          noEdit
          noExpand={false}
          expanded={warehouseSectionExpanded}
          onChangeExpanded={(expanded) => {
            try {
              localStorage.setItem('warehouseSectionExpanded', expanded.toString());
            } catch {} // eslint-disable-line no-empty
            setWarehouseSectionExpanded(expanded);
          }}
        />
        <Collapse in={warehouseSectionExpanded}>
          <WarehouseGrid isAdmin={isAdmin} warehouses={warehouses.data ?? []} />
        </Collapse>
        <FormTitle
          title={strings.staffList}
          noEdit
          noExpand={false}
          expanded={staffSectionExpanded}
          onChangeExpanded={(expanded) => {
            try {
              localStorage.setItem('staffSectionExpanded', expanded.toString());
            } catch {} // eslint-disable-line no-empty
            setStaffSectionExpanded(expanded);
          }}
        />
        <Collapse in={staffSectionExpanded}>
          <StaffTable staffList={staffList.data ?? []} />
        </Collapse>
      </div>
    </Layout>
  );
};
